<template lang="pug">
#permitted-function-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 施設関係者の使用機能の変更
    .box-body.register-body
      .message.mb-2
        span 変更したい機能の「使用する／使用しない」を変更して更新してください。
      .function-list.d-flex.flex-column.align-items-center
        .content.d-flex.flex-row.align-items-center(v-for="item in permittedFunctionItems")
          .icon.d-flex.flex-column.align-items-center
            img(:src="item.iconUrl")
            span.used(v-if="item.permitted == true") 使用中
            span.unused(v-else) 未使用
          .function.flex-grow-1
            .title {{ item.title }}
            .text {{ item.text }}
          .buttons.d-flex.flex-row-reverse
            PermittedFunctionSwitch(v-model="item.updatedPermitted")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoOtherUserPermittedFunctionEdit()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="使用機能を更新しますか？" @ok="doUpdate")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoOtherUserPermittedFunctionEdit")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import FacilitiesService, { FacilityGetModel, FacilityPatchModel, PermittedFunctions } from "@/commons/services/Facilities";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import PermittedFunctionSwitch from "@/pc/basics/PermittedFunctionSwitch.vue";

/**
 * 使用機能の確認画面コンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    OkModal,
    FormLabel,
    PermittedFunctionSwitch,
    PageLoading
  }
})
export default class OtherUserPermittedFunctionEdit extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** 施設 */
  facility: FacilityGetModel | null = null;

  /** 機能使用可否各行の情報 */
  permittedFunctionItems: Array<OtherUserPermittedFunctionEditItem> = [];

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // 使用機能可否は施設の情報なので施設情報を取得する
    const facility: FacilityGetModel = await this.getFacility();
    this.facility = facility;

    const permittedFunctionItems: Array<OtherUserPermittedFunctionEditItem> = this.createPermittedFunctionItems(facility);
    this.permittedFunctionItems = permittedFunctionItems;

    this.isBusy = false;

    return;
  }

  /**
   * ### 施設を取得する
   */
  public async getFacility(): Promise<FacilityGetModel> {
    const response: FacilityGetModel = await this.facilitiesService.get(userStore);
    return response;
  }

  /**
   * ### 機能の使用可否から目次の情報を作成する
   *
   * - 画像のURL等、保持していない情報が多いのでfor文等は使用できない
   */
  public createPermittedFunctionItems(facility: FacilityGetModel): Array<OtherUserPermittedFunctionEditItem> {
    let items: Array<OtherUserPermittedFunctionEditItem> = [
      {
        id: "otherUserOpinion",
        permitted: facility.permittedFunctions.otherUserOpinion,
        iconUrl: require("../../../assets/permitted-function-opinion-icon.png"),
        title: "施設関係者からの連絡",
        text: "施設関係者から施設に連絡・問い合わせが出来ます。施設では内容の確認と返信が出来ます。",
        linkUrl: "#opinion",
        updatedPermitted: facility.permittedFunctions.otherUserOpinion
      }
    ];

    return items;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    this.showUpdateModal = true;
    return;
  }

  /**
   * ### 使用機能を更新する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      if (this.facility) {
        const permittedFunctions: PermittedFunctions = {
          otherUserOpinion: this.permittedFunctionItems[0].updatedPermitted,
          // 家族の機能はここでは更新しない
          opinion: this.facility.permittedFunctions.opinion,
          reservation: this.facility.permittedFunctions.reservation,
          onlineReservation: this.facility.permittedFunctions.onlineReservation,
          outing: this.facility.permittedFunctions.outing,
          event: this.facility.permittedFunctions.event,
          careimage: this.facility.permittedFunctions.careimage,
          picture: this.facility.permittedFunctions.picture,
          // 待機者の機能はここでは更新しない
          waiterOpinion: this.facility.permittedFunctions.waiterOpinion
        };
        this.facility.permittedFunctions = permittedFunctions;

        const param: FacilityPatchModel = this.createUpdateParam(this.facility);
        await this.facilitiesService.patch(userStore, param);

        // 機能使用可否が変更されるのでstoreを更新する
        if (this.facility) userStore.updateFacility({ facility: this.facility });

        // 更新成功ダイアログを表示する
        this.showUpdateSuccessModal = true;
      }
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 施設更新パラメータ作成
   */
  public createUpdateParam(facility: FacilityGetModel): FacilityPatchModel {
    const params: FacilityPatchModel = {
      name: facility.name ? this.$stringUtil.convertYen(facility.name) : "",
      tel: facility.tel ? facility.tel : "",
      url: facility.url ? this.$stringUtil.convertYen(facility.url) : "",
      permittedFunctions: facility.permittedFunctions,
      useAdmissionApplicationTemplateId: facility.useAdmissionApplicationTemplateId,
      useAdmissionJudgementTemplateId: facility.useAdmissionJudgementTemplateId,
      recruitmentRoom: facility.recruitmentRoom,
      updated: facility.updated
    };

    return params;
  }

  /**
   * ### 使用機能の確認・変更に遷移する
   */
  public gotoOtherUserPermittedFunctionEdit(): void {
    this.$router.push(PcUrl.OtherUserPermittedFunctionList);
    return;
  }
}

interface OtherUserPermittedFunctionEditItem {
  /** ページ内リンク用のID */
  id: string;
  /** 機能の使用可否 */
  permitted: boolean;
  /** アイコン画像URL */
  iconUrl: string;
  /** 機能名 */
  title: string;
  /** 機能説明文 */
  text: string;
  /** ページ内リンクURL */
  linkUrl: string;
  /** 変更後機能の使用可否 */
  updatedPermitted: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// 親要素からネストしてスタイルを指定する
#permitted-function-edit {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .message {
    padding: 0 0 10px 10px;
  }

  // 使用機能の目次
  .function-list {
    width: 100%;

    .content {
      width: 100%;
      padding-top: 5px;
      padding-bottom: 5px;
      padding-left: 11px;
      padding-right: 11px;
      margin-bottom: 15px;
      border-bottom: solid 1px #cccccc;
      // アイコン
      .icon {
        min-width: 60px;
        width: 60px;
        margin-right: 12px;
        // 使用中・未使用
        .used,
        .unused {
          font-weight: bold;
        }
        .used {
          color: $font-key-color;
        }
        .unused {
          color: $font-disable-font-color;
        }
      }
      // 機能名
      .function {
        margin-right: 12px;
        .title {
          font-size: 18px;
          font-weight: bold;
        }
      }
      // ON/OFFスイッチ
      .buttons {
        width: 130px;
      }
    }
    .content:last-child {
      border-bottom: 0;
      margin-bottom: 0;
    }
  }
  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
